import ApiCollection            from '@widesk-core/models/ApiCollection';
import ImportModel               from '@models/map/ImportModel';
import ImportTypeModel           from '@models/map/ImportTypeModel';
import DxfLayersModel            from '@models/map/DxfLayersModel';
import DxfMappingModel           from '@models/map/DxfMappingModel';
import DxfPreviewModel           from '@models/map/DxfPreviewModel';
import PlanLayerPreviewModel     from '@models/map/PlanLayerPreviewModel';
import PlanAnalysisModel         from '@models/map/PlanAnalysisModel';
import { waitForPlanAnalysis }   from '@/MultiUserEditor/client/helpers/client/dxf/planAnalysisWait';
import { PlanAnalysisProgress }  from '@/MultiUserEditor/client/helpers/client/dxf/planAnalysisWait';
import PlanMatchPreviewModel     from '@models/map/PlanMatchPreviewModel';
import PlanShapesModel           from '@models/map/PlanShapesModel';
import PlanSourceModel           from '@models/map/PlanSourceModel';
import DxfRenderModel            from '@models/map/DxfRenderModel';
import DxfGenerateModel          from '@models/map/DxfGenerateModel';
import PlanGlyphsModel           from '@models/map/PlanGlyphsModel';
import { DxfPlacement }          from '@models/map/dxfTypes';
import { DxfPoi }                from '@models/map/dxfTypes';
import { DxfLayer }              from '@models/map/dxfTypes';
import { DxfSuggestedCategory }  from '@models/map/dxfTypes';
import { PlanGlyphReport }       from '@models/map/dxfTypes';
import { PlanGlyphScope }        from '@models/map/dxfTypes';
import { PlanImportTypeReference } from '@models/map/dxfTypes';
import { PlanRegion }             from '@models/map/dxfTypes';
import { PlanAnalysisResult }     from '@models/map/dxfTypes';
import { PlanDecorLayer }         from '@models/map/dxfTypes';
import { PlanMatchPreviewResult }  from '@models/map/dxfTypes';
import { PlanShapesResult }       from '@models/map/dxfTypes';
import { PlanSourceResult }       from '@models/map/dxfTypes';

export type DxfPreviewResult = {
	svgDataUri: string;
	width: number;
	height: number;
	pois: DxfPoi[];
	isGeoreferenced: boolean;
	suggestedPlacement: DxfPlacement | null;
};

export type DxfLayersResult = {
	layers: DxfLayer[];
	suggestedMapping: DxfSuggestedCategory[];
	suggestedDecor: PlanDecorLayer[];
};

/** Extensions servies par le format IMAGE, alignées sur ce que GD sait décoder côté serveur. */
const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp'];

/** Extensions acceptées par le champ de dépôt, dans l'ordre où on les documente. */
export const PLAN_FILE_ACCEPT = ['.dxf', '.svg', '.pdf', ...IMAGE_EXTENSIONS.map(e => `.${e}`)].join(',');

/**
 * Référence d'`ImportType` déduite de l'extension du fichier déposé.
 *
 * `map_image` couvre le plan qu'on n'a plus qu'en image : le DXF d'origine s'est perdu,
 * l'agence a fermé, il ne reste que le fichier envoyé aux exposants. Le serveur le traite
 * comme les autres — pseudo-calques par couleur au lieu de calques déclarés —, et tout
 * l'aval est identique.
 *
 * Le repli reste `map_dxf` : une extension inconnue est plus souvent un DXF mal nommé
 * qu'autre chose, et le serveur répond de toute façon un 422 explicite s'il ne sait pas
 * lire le fichier.
 */
export function planImportTypeReference(file: File): PlanImportTypeReference {
	const extension = file.name.split('.').pop()?.toLowerCase() ?? '';

	if (extension === 'svg') return 'map_svg';
	if (extension === 'pdf') return 'map_pdf';
	if (IMAGE_EXTENSIONS.includes(extension)) return 'map_image';

	return 'map_dxf';
}

/**
 * Crée l'Import de plan (multipart) puis renvoie son id.
 *
 * Le TYPE vient de l'extension : DXF, SVG ou PDF. Les trois suivent ensuite exactement
 * le même parcours côté serveur — le format n'est plus connu que du lecteur.
 *
 * Seule la carte cible est requise à la création ; le mapping des calques et les types
 * de POI sont fournis ensuite via `/plan/mapping`.
 */
export async function createPlanImport(params: {
	planFile: File;
	mapUrn: Urn;
	projectUrn: Urn;
	/**
	 * Zone entourée sur la carte (EPSG:3857) quand ce « plan » est une CAPTURE et non un fichier
	 * déposé — voir `services/map/docs/PLAN_ANALYSE_ZONE_CARTE.md`.
	 *
	 * Sa seule présence bascule l'import dans l'autre parcours : le serveur DÉDUIT le descripteur
	 * de placement à l'aperçu (`CapturePlacement`), et `generateDxf` n'a plus à en envoyer. C'est
	 * volontairement le serveur qui calcule — le front ne connaît pas le cadrage de la scène sur
	 * lequel ce descripteur s'exprime.
	 */
	captureBbox?: { minX: number; minY: number; maxX: number; maxY: number };
}): Promise<{ importId: number; typeReference: PlanImportTypeReference }> {
	const typeReference = planImportTypeReference(params.planFile);

	const importTypes = new ApiCollection(ImportTypeModel);
	await importTypes.setFilter('reference', typeReference).list();
	const importType = importTypes.first();
	if (!importType?.urn) {
		throw new Error(`Type d’import « ${typeReference} » introuvable : la migration a-t-elle été jouée ?`);
	}

	const model = new ImportModel();
	await model.patchFormData({
		file: params.planFile,
		project: params.projectUrn,
		importType: importType.urn,
		importInformations: JSON.stringify({
			mapUrn: params.mapUrn,
			...(params.captureBbox ? { captureBbox: params.captureBbox } : {}),
		}),
	});

	if (!model.id) {
		throw new Error('L’import n’a pas pu être créé.');
	}

	return { importId: Number(model.id), typeReference };
}

/** @deprecated Conservé pour l'ancienne sidebar ; préférer {@link createPlanImport}. */
export async function createDxfImport(params: {
	dxfFile: File;
	mapUrn: Urn;
	projectUrn: Urn;
}): Promise<number> {
	const { importId } = await createPlanImport({
		planFile: params.dxfFile,
		mapUrn: params.mapUrn,
		projectUrn: params.projectUrn,
	});

	return importId;
}

// Les opérations renvoient un DTO simple (pas une ressource JSON-LD) : on demande
// explicitement du JSON, dont la sérialisation expose bien les propriétés.
const JSON_OUT = { Accept: 'application/json' };

/** `POST /imports/{id}/plan/layers` : calques visibles + mapping suggéré (best-effort). */
export async function fetchDxfLayers(importId: number): Promise<DxfLayersResult> {
	const model = new DxfLayersModel();
	await model.setId(importId).save({ method: 'post', patchAttributes: {}, headers: JSON_OUT });

	return {
		layers: model.get('layers', []),
		suggestedMapping: model.get('suggestedMapping', []),
		suggestedDecor: model.get('suggestedDecor', []),
	};
}

/**
 * `POST /imports/{id}/plan/mapping` : persiste le mapping validé (préalable à l'aperçu).
 *
 * `analysisRegion` permet d'AJUSTER la zone d'analyse à cette étape, une fois les calques
 * connus. La première pose, elle, se fait à {@link savePlanSource} : cette opération-ci
 * exige un `layerMapping` valide, donc la connaissance des calques — que la zone sert
 * justement à assainir avant qu'on les regarde.
 *
 * Trois valeurs, trois sens, et la distinction n'est pas cosmétique :
 *   - `undefined` (défaut) → le champ n'est pas envoyé, la zone en place est CONSERVÉE ;
 *   - `null` → la zone est EFFACÉE, tout le document redevient analysable ;
 *   - un rectangle → il remplace la zone en place.
 *
 * Sans le premier cas, revalider un mapping effacerait la zone à chaque fois.
 *
 * ⚠️ **`matchFieldMapping` décide des doublons.** Il dit sur quel champ un POI déjà présent
 * est reconnu au réimport. Envoyé vide, le serveur retombe sur `'label'` — qui porte le nom de
 * l'exposant et non le numéro de stand —, et chaque réimport d'un hall RECRÉE les POI au lieu
 * de les mettre à jour, sans rien signaler. C'est le défaut le plus coûteux du parcours ; il
 * se mesure avec {@link fetchPlanMatchPreview} avant d'être choisi.
 *
 * `decorLayers` remplace le décor enregistré ; `undefined` le laisse en place.
 */
export async function saveDxfMapping(
	importId: number,
	layerMapping: DxfSuggestedCategory[],
	categoryPlaceTypeMapping: Record<string, string>,
	matchFieldMapping: Record<string, string>,
	analysisRegion?: PlanRegion | null,
	decorLayers?: PlanDecorLayer[],
): Promise<void> {
	const model = new DxfMappingModel();
	await model.setId(importId).save({
		method: 'post',
		patchAttributes: {
			layerMapping,
			categoryPlaceTypeMapping,
			matchFieldMapping,
			// Un rectangle VIDE est la façon d'effacer : le serveur distingue « champ absent »
			// (conserver) de « rectangle dégénéré » (effacer).
			...(undefined === analysisRegion ? {} : { analysisRegion: analysisRegion ?? {} }),
			...(undefined === decorLayers ? {} : { decorLayers }),
		},
		headers: JSON_OUT,
	});
}

/**
 * `POST /imports/{id}/plan/source` : l'aperçu du plan ENTIER, sur lequel se trace la zone
 * d'analyse.
 *
 * À appeler AVANT `/plan/layers` : sans zone, le cartouche, la légende et les marges d'un
 * plan scanné fabriquent de faux calques dans l'écran de mapping, et faussent au passage la
 * palette et les statistiques de forme calculées sur tout le document.
 *
 * L'aperçu montre TOUJOURS le document entier, zone déjà posée ou non : on ne peut pas
 * élargir un cadre qu'on ne voit plus.
 */
async function postPlanSource(
	importId: number,
	patchAttributes: Record<string, unknown>,
): Promise<PlanSourceResult> {
	const model = new PlanSourceModel();
	await model.setId(importId).save({ method: 'post', patchAttributes, headers: JSON_OUT });

	return {
		imageDataUri: model.get('imageDataUri', ''),
		width: model.get('width', 0),
		height: model.get('height', 0),
		analysisRegion: model.get('analysisRegion', null),
	};
}

/** Aperçu seul, sans rien enregistrer. */
export function fetchPlanSource(importId: number): Promise<PlanSourceResult> {
	return postPlanSource(importId, {});
}

/**
 * Enregistre la zone d'analyse et renvoie l'aperçu à jour.
 *
 * `region` en pixels de l'aperçu ; `null` EFFACE la zone et fait réanalyser tout le
 * document — c'est le seul moyen de revenir en arrière.
 */
export function savePlanSource(importId: number, region: PlanRegion | null): Promise<PlanSourceResult> {
	// Un rectangle vide est la convention d'effacement du serveur, qui distingue « champ
	// absent » (conserver) de « rectangle dégénéré » (effacer).
	return postPlanSource(importId, { analysisRegion: region ?? {} });
}

/** `POST /imports/{id}/plan/preview` : fond + POI (transform gelée côté serveur). */
export async function previewDxf(importId: number): Promise<DxfPreviewResult> {
	const model = new DxfPreviewModel();
	await model.setId(importId).save({ method: 'post', patchAttributes: {}, headers: JSON_OUT });

	return {
		svgDataUri: model.get('svgDataUri', ''),
		width: model.get('width', 0),
		height: model.get('height', 0),
		pois: model.get('pois', []),
		isGeoreferenced: model.get('isGeoreferenced', false),
		suggestedPlacement: model.get('suggestedPlacement', null),
	};
}

/**
 * `POST /imports/{id}/plan/layer-preview` : rend UN calque, seul, pour le prévisualiser sur la
 * carte à l'étape de mapping.
 *
 * `layer` est le nom BRUT renvoyé par `/plan/layers` — y compris un pseudo-calque de style
 * (`fill … · stroke … · …`). L'étiquette « Forme 1 » affichée par l'UI n'existe que côté front.
 */
export async function fetchPlanLayerPreview(importId: number, layer: string, shape?: number | null): Promise<{
	svgDataUri: string;
	width: number;
	height: number;
}> {
	const model = new PlanLayerPreviewModel();
	// `shape` absent = le calque entier, comportement d'origine. Présent, le serveur ne rend
	// que cette forme-là, cadrée sur elle-même.
	const patchAttributes = null === shape || undefined === shape ? { layer } : { layer, shape };
	await model.setId(importId).save({ method: 'post', patchAttributes, headers: JSON_OUT });

	return {
		svgDataUri: model.get('svgDataUri', ''),
		width: model.get('width', 0),
		height: model.get('height', 0),
	};
}

/**
 * `POST /imports/{id}/plan/shapes` : TOUTES les formes importables du plan, en vecteur.
 *
 * C'est la matière de l'aperçu global de l'étape de mapping — celui qu'on pose sur la carte et
 * sur lequel l'opérateur décoche au clic. Rien à voir avec {@link fetchPlanLayerPreview}, qui
 * rend UNE image d'UN calque : une image ne se clique pas forme par forme.
 *
 * `layers` restreint la lecture aux calques affichés (un SVG de salon porte 28 000 tracés). Ce
 * filtre ne décale AUCUN rang : ils sont établis par calque, côté serveur.
 */
export async function fetchPlanShapes(importId: number, layers?: string[]): Promise<PlanShapesResult> {
	const model = new PlanShapesModel();
	await model.setId(importId).save({
		method: 'post',
		patchAttributes: layers?.length ? { layers } : {},
		headers: JSON_OUT,
	});

	return {
		shapes: model.get('shapes', []),
		width: model.get('width', 0),
		height: model.get('height', 0),
	};
}

/**
 * `POST /imports/{id}/plan/analysis` : **tout ce que l'écran de vérification affiche**
 * (`docs/PLAN_PARCOURS_IMPORT.md` §3), désormais par une analyse ASYNCHRONE
 * (`docs/PLAN_ANALYSE_ASYNC.md`).
 *
 * ## Une seule promesse, trois échanges
 *
 * Le POST ne rend plus l'analyse : il la LANCE et répond 202 avec un reçu. Cette fonction
 * enchaîne donc le lancement, l'attente ({@link waitForPlanAnalysis} : Mercure pour la
 * progression, relecture périodique pour la garantie) et la lecture du résultat. L'appelant,
 * lui, voit exactement ce qu'il voyait — une promesse qui rend un `PlanAnalysisResult` — plus
 * un `onProgress` facultatif pour dire où en est le travail.
 *
 * ⚠️ **`source.imageDataUri` ne voyage plus avec l'analyse** : il pèse ~400 ko et l'écran l'a
 * déjà reçu de {@link fetchPlanSource} à l'étape précédente, donc le serveur ne le range pas.
 * Passer `imageDataUri` dans les options le réinjecte dans le résultat ; sans lui, le champ
 * revient vide et l'aperçu du panneau s'efface.
 *
 * ## Pourquoi une seule fonction là où il y en avait quatre
 *
 * Le parcours enchaînait `fetchPlanSource` → `savePlanSource` → `fetchDxfLayers` →
 * `fetchPlanShapes` → `fetchPlanGlyphs`. Chaque appel **relit et reparse le fichier** côté
 * serveur, et l'ORDRE entre eux est une règle métier que ce module portait dans ses
 * commentaires plutôt que le serveur dans son code. Un écran unique n'était pas tenable tant
 * qu'il fallait orchestrer six requêtes et gérer leurs échecs partiels.
 *
 * Les quatre opérations restent servies : rien n'oblige à migrer d'un bloc, et l'ancien
 * parcours continue de fonctionner à l'identique.
 *
 * ## `region`, trois valeurs, trois sens
 *
 * Exactement la convention de {@link savePlanSource}, et il ne faut surtout pas la
 * réinventer :
 *   - `undefined` (défaut) → champ non envoyé, la zone en place est CONSERVÉE ;
 *   - `null` → la zone est EFFACÉE, tout le document redevient analysable ;
 *   - un rectangle → il remplace la zone en place.
 *
 * Sans le premier cas, un simple rafraîchissement de l'écran effacerait le cadre que
 * l'opérateur vient de tracer.
 *
 * `layers` n'est qu'un filtre de VOLUME sur les formes rendues : il ne décale aucun rang.
 */
export async function analysePlan(
	importId: number,
	options: {
		region?: PlanRegion | null;
		layers?: string[];
		/** L'aperçu déjà en main, que le serveur ne renvoie plus — voir plus haut. */
		imageDataUri?: string;
		onProgress?: (progress: PlanAnalysisProgress) => void;
		signal?: AbortSignal;
	} = {},
): Promise<PlanAnalysisResult> {
	const launch = new PlanAnalysisModel();
	await launch.setId(importId).save({
		method: 'post',
		patchAttributes: {
			...(undefined === options.region ? {} : { analysisRegion: options.region ?? {} }),
			...(options.layers?.length ? { layers: options.layers } : {}),
		},
		headers: JSON_OUT,
	});

	const analysis = await waitForPlanAnalysis(importId, launch.get('planAnalysisId', 0), {
		onProgress: options.onProgress,
		signal: options.signal,
	});

	// `result` est nul tant que l'analyse n'a pas abouti — or on n'arrive ici qu'une fois
	// `DONE`, un échec ayant déjà levé avec son motif.
	const result = analysis.result ?? null;
	const source = result?.source;

	return {
		source: {
			...(source ?? { width: 0, height: 0, analysisRegion: null }),
			// L'aperçu ne voyage plus avec l'analyse : c'est l'appelant qui le porte depuis
			// `/plan/source`. Une chaîne vide plutôt qu'un `undefined` — tout l'aval le traite
			// comme un `src`.
			imageDataUri: source?.imageDataUri || options.imageDataUri || '',
		} as PlanSourceResult,
		layers: result?.layers ?? [],
		suggestedMapping: result?.suggestedMapping ?? [],
		suggestedDecor: result?.suggestedDecor ?? [],
		shapes: result?.shapes ?? [],
		// Absent du JSON quand il n'y a rien à étiqueter : le serveur omet les propriétés
		// nulles. Un rapport à zéro se lirait « 0 % des numéros lisibles », ce qui est faux.
		glyphs: result?.glyphs ?? null,
		doubts: result?.doubts ?? [],
		counters: result?.counters ?? { kept: 0, doubtful: 0, discarded: 0 },
		truncated: result?.truncated ?? 0,
	};
}

/**
 * `POST /imports/{id}/plan/match-preview` : **ce que chaque champ candidat donnerait** au
 * rapprochement des POI existants (`docs/PLAN_PARCOURS_IMPORT.md` §5).
 *
 * Personne ne sait de tête où vivent les numéros de stand : sur un plan de salon, `label`
 * porte le nom de l'exposant. Le serveur, lui, sait l'essayer — pour chaque champ, combien de
 * POI existants les numéros lus sur le plan retrouveraient.
 *
 * Exige un `layerMapping` déjà enregistré (c'est lui qui désigne les calques de numéros) ; les
 * types de POI passent dans le corps, pour pouvoir comparer AVANT de valider le mapping.
 */
export async function fetchPlanMatchPreview(
	importId: number,
	categoryPlaceTypeMapping: Record<string, string>,
): Promise<PlanMatchPreviewResult> {
	const model = new PlanMatchPreviewModel();
	await model.setId(importId).save({
		method: 'post',
		patchAttributes: { categoryPlaceTypeMapping },
		headers: JSON_OUT,
	});

	return {
		candidates: model.get('candidates', []),
		numbers: model.get('numbers', 0),
	};
}

/** `POST /imports/{id}/plan/render` : re-rendu du fond sans les POI exclus. */
export async function renderDxf(importId: number, excludedHandles: string[]): Promise<string> {
	const model = new DxfRenderModel();
	await model.setId(importId).save({ method: 'post', patchAttributes: { excludedHandles }, headers: JSON_OUT });

	return model.get('svgDataUri', '');
}

/**
 * `POST /imports/{id}/plan/generate` : enregistre le placement et génère les features.
 * La génération est SYNCHRONE côté serveur : le POST ne répond qu'une fois l'import
 * terminé.
 *
 * `placement` est `null` pour une CAPTURE de zone de carte : le serveur l'a déjà déduit de la zone
 * entourée en gelant sa transform (`CapturePlacement`), et n'accepterait de toute façon pas mieux
 * — le cadrage sur lequel ce descripteur s'exprime est celui de la scène, que le front ignore. Le
 * champ est alors OMIS du corps, jamais envoyé vide : côté serveur, « absent » veut dire « garde
 * celui que tu as ».
 */
export async function generateDxf(
	importId: number,
	placement: DxfPlacement | null,
	excludedHandles: string[],
): Promise<void> {
	const model = new DxfGenerateModel();
	await model.setId(importId).save({
		method: 'post',
		patchAttributes: { ...(placement ? { placement } : {}), excludedHandles },
	});
}

/**
 * Lit le statut final de l'import. La génération étant synchrone (`generateDxf` répond
 * une fois terminée), un seul GET suffit — pas de polling.
 */
export async function fetchImportStatus(importId: number): Promise<'done' | 'error'> {
	const model = new ImportModel();
	await model.setId(importId).fetch();

	// La référence d'état vient de EnumStateTrait::getReference() = nom de la case
	// PHP, donc en MAJUSCULES ("DONE"). On compare insensible à la casse pour ne pas
	// repasser dans la branche d'erreur alors que la génération a réussi.
	return model.importStatus?.reference?.toUpperCase() === 'DONE' ? 'done' : 'error';
}

/**
 * `POST /imports/{id}/plan/glyphs` : dictionnaire de glyphes.
 *
 * Sans `entries` c'est une lecture, avec `entries` un enregistrement suivi du rapport
 * rafraîchi — d'où une seule fonction. L'appel exige un mapping déjà enregistré : c'est
 * lui qui désigne les calques de numéros.
 */
async function postPlanGlyphs(
	importId: number,
	patchAttributes: Record<string, unknown>,
): Promise<PlanGlyphReport> {
	const model = new PlanGlyphsModel();
	await model.setId(importId).save({ method: 'post', patchAttributes, headers: JSON_OUT });

	return {
		fontKey: model.get('fontKey', ''),
		totalGlyphs: model.get('totalGlyphs', 0),
		resolvedGlyphs: model.get('resolvedGlyphs', 0),
		totalLabels: model.get('totalLabels', 0),
		readableLabels: model.get('readableLabels', 0),
		glyphCoverage: model.get('glyphCoverage', 0),
		labelCoverage: model.get('labelCoverage', 0),
		unresolved: model.get('unresolved', []),
	};
}

/** Signatures restant à étiqueter pour ce plan (lecture seule). */
export function fetchPlanGlyphs(importId: number): Promise<PlanGlyphReport> {
	return postPlanGlyphs(importId, {});
}

/**
 * Enregistre l'étiquetage saisi par l'opérateur et renvoie le rapport rafraîchi.
 *
 * ⚠️ Le serveur IGNORE silencieusement une entrée invalide plutôt que de rejeter tout
 * l'étiquetage (une frappe ne doit pas faire perdre le reste). Le rapport renvoyé est
 * donc la seule source de vérité sur ce qui a réellement été retenu.
 *
 * @param entries signature => caractère, un seul caractère parmi `[0-9A-Z.- ]`
 */
export function savePlanGlyphs(
	importId: number,
	entries: Record<string, string>,
	scope: PlanGlyphScope = 'project',
): Promise<PlanGlyphReport> {
	return postPlanGlyphs(importId, { entries, scope });
}
